import Dropdown from 'bootstrap/js/dist/dropdown';


/**
 * Monitor the navbar and when it scolls down set it to the sticky version
 */
export const watchNavbarScroll = () => {

    const header = document.getElementById('header');
    const trigger = document.querySelector('.header-scroll-trigger');

    const handler = (entries) => {

        // entries is an array of observed dom nodes
        // we're only interested in the first one at [0]
        // because that's our .sentinal node.
        // Here observe whether or not that node is in the viewport
        if (!entries[0].isIntersecting) {
            header.classList.add('header-scrolled');
        }
        else {
            header.classList.remove('header-scrolled');
        }
    }

    if (trigger) {
        // create the observer
        const observer = new IntersectionObserver(handler);

        // give the observer some dom nodes to keep an eye on
        observer.observe(trigger);
    }
}

/**
 * Monitor the header search bar and assist with classes where state required
 */
export const watchNavbarSearch = () => {

    // Add event listener to nav button
    const header = document.getElementById('header');
    const searchInput = document.querySelector('.nav-search .form-control');    
    const searchButton = document.querySelector(".nav-search .nav-search-button");
    const searchGroup = document.querySelector('.nav-search .input-group');   

    const exitSearch = (e) => {
        const currentTarget = e.currentTarget;        

        // Give browser time to focus the next element
        requestAnimationFrame(() => {            

            // Check if the new focused element is a child of the original container
            if (!currentTarget.contains(document.activeElement)) {

                // leaving the search group; reset
                searchInput.value = "";
                header.classList.remove('nav-search-visible');
            }
        });
    };

    const enterSearch = (e) => {

        // dont attempt to re-show/add classes if we're already active
        if (false === header.classList.contains('nav-search-visible')) {
            header.classList.add('nav-search-visible');
            searchInput.focus();
        }       
    }

    if (searchGroup)  {
        
        searchButton.addEventListener("click", (e) => {

            // no search term yet or search hidden
            if (searchInput.value == "") {
                e.preventDefault();
                enterSearch(e);
            }

        }, false);

        searchGroup.addEventListener("focusin", enterSearch, false);
        searchGroup.addEventListener("focusout", exitSearch, false);
    }    
}

/**
 * Open a dropdown on click, required to prevent hover state causing click to stop working
 */
 export const dropdownToggleClicked = () => {

    Array.from(document.querySelectorAll('.dropdown-toggle.dropdown-toggle-hover')).forEach(i => {
        
        const dropdown = Dropdown.getOrCreateInstance(i);
        const dropdownClicked = "art-dropdown-clicked";
        const className = "art-dropdown-open";
        const parentClassName = ".nav-primary";

        const showDropdown = (event) => {
            event.preventDefault();

            if (!i.hasAttribute(dropdownClicked)) {
                dropdownClear();
                dropdown.show();
                
                if (event.type !== 'shown.bs.dropdown') {
                    i.setAttribute(dropdownClicked, "");
                }
            } else {
                i.removeAttribute(dropdownClicked, "");
            }
            
            // Clear focus to stop extra outline styles from happening
            document.activeElement.blur();
        }

        const hideDropdown = () => {
            const parent = i.closest(parentClassName);
            if (parent != null) {
                parent.classList.remove(className);
            }

            // Slightly delay attribute removal to allow responsive animations to ocurr
            setTimeout(() => {
                i.removeAttribute(dropdownClicked);
                dropdown.hide();
            }, 400);
        }

        // For device compatibility we need to run either click or touchstart events
        let eventType = ('ontouchstart' in window) ? "touchend" : "click";

        // Open the dropdown on tap only, do not open when a user swipes
        let touchMoved = false;
        i.addEventListener(eventType, (event) => {
            if (!touchMoved) {
                showDropdown(event);
            }
            
        }, false);


        // show and hide menus for keyboard users
        i.addEventListener('keydown', (event) => {

            if (event.key === "Enter") {
                event.preventDefault();                
                showDropdown(event);
            }          
            
            if(event.key === "Escape") {
                event.preventDefault();                
                hideDropdown(event); 
            }
            
        }, false);


        i.nextElementSibling.addEventListener(eventType, (event) => {
            if (!touchMoved) {
                hideDropdown(event); 
            }
        }, false);

        // Detect swipe event and don't allow swiping to open or close the dropdown
        if ('ontouchstart' in window) {
            i.addEventListener('touchmove', () => { touchMoved = true }, false);
            i.nextElementSibling.addEventListener('touchmove', () => { touchMoved = true }, false);
            
            i.addEventListener('touchstart', () => { touchMoved = false }, false);
            i.nextElementSibling.addEventListener('touchstart', () => { touchMoved = false }, false);
        }

        // Remove the data attribute if a clickEvent was called on bs dropdown hide
        i.parentElement.addEventListener('hide.bs.dropdown', (event) => {
            if (event && event.clickEvent) {
                hideDropdown(event);
            }
        });

        i.parentElement.addEventListener('show.bs.dropdown', (event) => {
            // Only apply this to the nav-primary using the below check with delay to allow animations
            const parent = i.closest(parentClassName);
            if (parent != null) {
                setTimeout(() => {
                    parent.classList.add(className);
                }, 400);
            }
        });
    });
}

/**
 * Open a dropdown on hover (default Bootstrap allows dropdown to open on click only)
 */
 export const dropdownToggleHover = () => {

    // timings for show/hide
    const timeBetweenMenuItems = 300;
    const timeBeforeHideMenu = 500;
    const timeBeforeShowMenu = 500;
    const dropdownClicked = "art-dropdown-clicked";
    const dropdownScrolled = "art-dropdown-scrolled";
    const header = document.getElementById('header');


    // timers
    let menuExitTimer;

    // there are 2 separate menus in the header
    const menus = document.querySelectorAll('.nav-primary, .nav-external-links');    
    const menuItems = document.querySelectorAll('.dropdown-toggle.dropdown-toggle-hover');

    if (!menus || !menuItems) {
        return;
    }

    // when the mouse leaves the mega menu
    Array.from(menus).forEach((menu) => {     
        menu.addEventListener("mouseleave", () => {

            if(isDesktop()) {

                // get the open menu item if there is one
                const openMenuItem = Array.from(menuItems).find((item) => item.matches('.show'));

                if (openMenuItem) {

                    // wait for a period before closing
                    menuExitTimer = setTimeout(() => {   

                        openMenuItem.removeAttribute(dropdownClicked, "");
                        Dropdown.getOrCreateInstance(openMenuItem).hide();

                        // Clear focus to stop extra outline styles from being applied
                        if(! header.classList.contains('nav-search-visible')) {
                            document.activeElement.blur();
                        }

                    }, timeBeforeHideMenu);
                }
            }
        });
    });

    // when the mouse enters the mega menu
    Array.from(menus).forEach((menu) => {
        menu.addEventListener("mouseenter", () => {    
            if (menuExitTimer) {

                // keep it open; user may have moused outside menu
                clearTimeout(menuExitTimer);
            }        
        });
    });    

    Array.from(menuItems).forEach(i => {
        
        const dropdown = Dropdown.getInstance(i);     

        // timer for when to show dropdown
        let showDropdown;            

        // on-scroll always hide the dropdown, if desktop menu is active
        addEventListener("scroll", (event) => {                       
            if (dropdown && !isMobile()) {
                i.setAttribute(dropdownScrolled, "true");
                i.removeAttribute(dropdownClicked, "");
                dropdown.hide();
                
                // Clear focus to stop extra outline styles from happening on desktop
                if(! header.classList.contains('nav-search-visible') && isDesktop()) {
                    document.activeElement.blur();
                }
            }
        });        

        // Show dropdown on hovering over the dropdown toggle
        i.parentElement.addEventListener("mouseenter", () => {    
            
            // Check this is a desktop menu
            if(isDesktop()) {
            
                if (!i.hasAttribute(dropdownClicked)) {                

                    // have a short delay if moving from another megamenu item
                    const isMegaMenuLink = document.activeElement.matches(".nav-link.dropdown-toggle.dropdown-toggle-hover");
                    const timeToShow = isMegaMenuLink ? timeBetweenMenuItems : timeBeforeShowMenu;

                    // debounce the opening of the menu; add a menu activation delay                
                    showDropdown = setTimeout(() => {

                        dropdownClear();
                        dropdown.show();
                        i.setAttribute(dropdownScrolled, "false");                        

                        // note this is moved to when mouseout whole menu, not item
                        // Clear focus to stop extra outline styles from being applied
                        //document.activeElement.blur();                                            
                        
                    }, timeToShow);                
                }
            }
        }, false);

        // Remove dropdown when leaving the dropdown menu
        i.parentElement.addEventListener("mouseleave", () => {
            
            // Check this is desktop menu
            if(isDesktop()) {

                // clear the activation delay
                // opening the menu may have not been the intended action
                clearTimeout(showDropdown);
                
                if (!i.hasAttribute(dropdownClicked)) {                                            
                    
                    const hideDropdown = setTimeout(() => {

                        // Hide the dropdown if we haven't clicked to keep open
                        // don't immediately hide the dropdown if the user to moving sideways to other menu top level megamenu items
                        // provide a delay so that the current menu is shown, until the timeout occurs
                        // we need to wait until focus has been set onto this item and off the siblings as there is a delay
                        let isMegaMenuLink = document.activeElement.matches(".nav-link.dropdown-toggle.dropdown-toggle-hover");
                        if (false === isMegaMenuLink) {                        
                            dropdown.hide();
                        }
                        
                    }, timeBeforeHideMenu);                

                    // Stop the dropdown from hiding if we have hovered over the dropdown-menu content
                    i.parentElement.querySelector('.dropdown-menu').addEventListener("mouseenter", (event) => {
                        if (!event.target.hasAttribute(dropdownClicked)) {
                            clearTimeout(hideDropdown);                        
                        }
                    }, false);
                }
            }

        }, false);        
    });
}

/**
 * Clear dropdown state
 */
const dropdownClear = () => {

    const dropdownClicked = "art-dropdown-clicked";
    const dropdownScrolled = "art-dropdown-scrolled";

    // Hide all dropdowns and remove dropdown clicked attributes
    Array.from(document.querySelectorAll('#nav-menu:not(.show) .dropdown-toggle.dropdown-toggle-hover')).forEach(i => {

        i.removeAttribute(dropdownClicked, "");
        i.setAttribute(dropdownScrolled, "false");

        let dropdown = Dropdown.getOrCreateInstance(i);
        dropdown.hide();        
    });
}

const isDesktop = () => {

    if(!document.querySelector('.offcanvas.show') && !document.querySelector('.offcanvas.showing')) {
        return true;
    }

    return false;
}

const isMobile = () => {
    const bs5el = document.querySelector('.art-theme-bs5 .navbar-expand-lg .offcanvas');
    return bs5el ? getComputedStyle(bs5el).position === 'fixed' : false;
}


/**
 * Monitor the header search bar and add a class to the grandparent when its active
 */
 export const watchSearchFocus = () => {

    const searchInput = document.querySelector(".offcanvas-body .nav-search .form-control");

    if (!searchInput) {
        return;
    }

    const scrollableArea = searchInput.closest(".offcanvas");
    const className = "nav-search-focused";

    searchInput.addEventListener("focusin", () => {
        scrollableArea.classList.add(className);

    });

    searchInput.addEventListener("focusout", () => {
        scrollableArea.classList.remove(className);
    });        
 }